import React, { useState } from 'react';
import { CalendarX2, LogOut } from 'lucide-react';
import { useAuth } from '@contexts/AuthContext';
import { useProfile } from '@contexts/ProfileContext';
import { Button } from './Button';
import { formatDateBR } from '@/utils/formatters';

const SUPPORT_EMAIL = 'suporte@giannotti.com.br';

/**
 * Tela cheia mostrada quando `access_status = expired` (período de teste encerrado ou acesso com
 * data de término no passado). O backend responde 403 `access_expired` em tudo, exceto
 * /auth/me, /auth/profile e /auth/logout — então aqui só cabe explicar e oferecer "Sair".
 */
export const AccessExpiredView: React.FC = () => {
  const { profile } = useProfile();
  const { logout } = useAuth();
  const [leaving, setLeaving] = useState(false);

  const until = profile?.access_until ? formatDateBR(profile.access_until) : '';
  const wasTrial = profile?.plan === 'trial';

  const handleLogout = async () => {
    setLeaving(true);
    try {
      await logout();
    } catch (err) {
      console.error('Erro no logout:', err);
      setLeaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center gradient-login-bg p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-card p-6 sm:p-8 animate-slide-up text-center">
          <img src="/plan_logo.svg" alt="Plan" className="w-16 h-16 rounded-xl mx-auto mb-4" />
          <div className="w-12 h-12 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mx-auto mb-4">
            <CalendarX2 size={24} />
          </div>
          <h1 className="text-h3">
            {wasTrial && until ? `Seu período de teste terminou em ${until}` : until ? `Seu acesso terminou em ${until}` : 'Seu acesso está encerrado'}
          </h1>
          <p className="text-slate-600 mt-3 [overflow-wrap:anywhere]">
            Para continuar usando o Plan, fale com{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-emerald-700 hover:underline font-medium">
              {SUPPORT_EMAIL}
            </a>
            . Seus dados ficam guardados e voltam a aparecer assim que o acesso for liberado.
          </p>
          {profile?.email && <p className="text-small text-slate-400 mt-2 [overflow-wrap:anywhere]">Conta: {profile.email}</p>}

          <Button variant="outline" onClick={handleLogout} loading={leaving} icon={<LogOut />} className="w-full mt-6">
            Sair
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccessExpiredView;
